import React from 'react';
import { Body, Box, Button, Card, Container, Flex, Grid, GridItem, Heading, Link } from '@iris/react-components';
import { atcBaseUrl } from '../../constants/config';
import { sendClick } from 'reaxl-analytics';
import { CheckBadgeIcon, ChecklistIcon, GaugeIcon, MoneyIcon } from '@iris/react-icons';

// iris-override (type gap): Iris Link/Button public types omit standard DOM mouse events
// (e.g. onMouseDown) even though both components forward unknown props to the
// underlying <a>/<button>. Cast to bypass strict typing.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const AnyLink = Link as any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const AnyButton = Button as any;

export default function WeMakeTrustAPrioritySection() {

  // Pixall Event C5. Learn More In Our FAQ Button															
  const onFaqClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const pixallData = {
      hasListingType: false,
      label: 'cta::learn-more-in-our-faq',
      eventSource: 'button',
      pageId: 'slp_psx_buy',
      cdl: {
          event: {
              action: 'Click',
              element: 'Learn More In Our FAQ Button',
              tagIndex: 'psx-124',
          },
      },
    };
    sendClick('sycClickEvent', event, {
        isPixallEvent: true,
        pixallData,
    });

    globalThis.open(`${atcBaseUrl}/marketplace/faqs`, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box className="we-make-trust-a-priority" data-testid="we-make-trust-section">
      <Container maxWidth="lg" className="trust-section-card iris:!mx-auto">
        <Grid className="trust-cards-grid" columns={{ xs: 1, md: 2 }}>
          <GridItem colSpan={{ xs: 1, md: 2 }} className="iris:p-0">
            <Flex justify="center">
              <Heading as="h2" size="xxl" className="trust-section-heading">We Make Trust a Priority</Heading>
            </Flex>
          </GridItem>

          {/* Identity Check */}
          <GridItem className="iris:p-0">
            <Card variant="subtle" className="trust-card" data-testid="trust-card-identity">
              <Flex className="trust-card-header" justify="between" align="start">
                <Heading as="h3" size="lg">Identity Check</Heading>
                <ChecklistIcon
                  className="trust-card-icon"
                  aria-label="Identity Check"
                  aria-hidden={false}
                  role="img"
                  width={24}
                  height={24}
                />
              </Flex>
              <Body as="p">
                We verify our buyers and sellers by IP addresses, email, phone numbers, and scan IDs using
                {' '}
                <AnyLink
                  variant="inline"
                  href="https://www.stripe.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  onMouseDown={(event: React.MouseEvent) => event.preventDefault()}
                  className="stripe-link"
                >
                  Stripe secure platform
                </AnyLink>
                .
              </Body>
            </Card>
          </GridItem>

          {/* Vehicle History Check */}
          <GridItem className="iris:p-0">
            <Card variant="subtle" className="trust-card" data-testid="trust-card-vehicle-history">
              <Flex className="trust-card-header" justify="between" align="start">
                <Heading as="h3" size="lg">Vehicle History Check</Heading>
                <GaugeIcon
                  className="trust-card-icon"
                  aria-label="Vehicle History Check"
                  aria-hidden={false}
                  role="img"
                  width={24}
                  height={24}
                />
              </Flex>
              <Body as="p" className="iris:hidden iris:md:block">Every vehicle listing includes a free AutoCheck history report so buyers can review ownership and accident history.</Body>
              <Body as="p" className="iris:md:hidden">We confirm the car&apos;s ownership and accident history so there are no surprises when you buy.</Body>
            </Card>
          </GridItem>

          {/* Secure Payment */}
          <GridItem className="iris:p-0">
            <Card variant="subtle" className="trust-card" data-testid="trust-card-secure-payment">
              <Flex className="trust-card-header" justify="between" align="start">
                <Heading as="h3" size="lg">Secure Payment</Heading>
                <MoneyIcon
                  className="trust-card-icon"
                  aria-label="Secure Payment"
                  aria-hidden={false}
                  role="img"
                  width={24}
                  height={24}
                />
              </Flex>
              <Body as="p">We collect payment from the buyer and pay the seller electronically (wire, ACH, credit/debit card).</Body>
            </Card>
          </GridItem>

          {/* Guaranteed Title */}
          <GridItem className="iris:p-0">
            <Card variant="subtle" className="trust-card" data-testid="trust-card-guaranteed-title">
              <Flex className="trust-card-header" justify="between" align="start">
                <Heading as="h3" size="lg" className="iris:hidden iris:md:block">Clean Title Guarantee</Heading>
                <Heading as="h3" size="lg" className="iris:md:hidden">Guaranteed Title</Heading>
                <CheckBadgeIcon
                  className="trust-card-icon"
                  aria-label="Guaranteed Title"
                  aria-hidden={false}
                  role="img"
                  width={24}
                  height={24}
                />
              </Flex>
              <Body as="p" className="iris:hidden iris:md:block">We do not allow vehicles for sale with salvage titles. You&apos;ll be guaranteed a clean, lien-free title by choosing our Full Service checkout option.</Body>
              <Body as="p" className="iris:md:hidden">We pay off the seller&apos;s loan so buyers always receive the title.</Body>
            </Card>
          </GridItem>

          {/* FAQ CTA */}
          <GridItem colSpan={{ xs: 1, md: 2 }} className="iris:p-0">
            <Box className="faq-cta-container">
              <AnyButton
                className="faq-cta-button"
                variant="tertiary"
                size="lg"
                href={`${atcBaseUrl}/marketplace/faqs`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onFaqClick}
                onMouseDown={(event: React.MouseEvent) => event.preventDefault()}
                data-testid="faq-link"
              >
                Learn More in our FAQ
              </AnyButton>
            </Box>
          </GridItem>
        </Grid>
      </Container>
    </Box>
  );
}
